// DOM guard against the "NotFoundError: Failed to execute 'removeChild' on
// 'Node'" crash (also its sibling 'insertBefore' variant).
//
// Symptom (seen in Sentry, deskte-spa, 2026-07-13):
//   NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be
//   removed is not a child of this node.
//   at OT ... Qs ... LT ...   ← every frame is inside React's minified
//   reconciler; there is NO Deskte app frame in the stack.
//
// Cause: a DOM-mutating browser EXTENSION — overwhelmingly Google Translate,
// also some ad-blockers / accessibility overlays — replaces the page's text
// nodes with its own wrapper nodes. React still holds a reference to the
// ORIGINAL text node and, on the next update, calls parent.removeChild(orig)
// (or insertBefore relative to it). Because the extension already detached
// that node, the browser throws NotFoundError, React's whole render tree
// unmounts, and our ErrorBoundary shows the "Algo salió mal" screen —
// forcing the agent to reload mid-workday. This is a known React issue
// (facebook/react#11538); it is NOT a bug in our code and cannot be fixed
// from a component, because the failing call is deep inside React itself.
//
// Fix: make removeChild / insertBefore no-op safely when the node isn't
// actually a child of the given parent, instead of throwing. This is the
// mitigation recommended in that React issue and used widely in production
// React apps that must tolerate Google Translate. It only changes behavior
// in the exact error case (parent mismatch) — every normal DOM operation is
// forwarded untouched to the native method — so it is safe and reversible.
//
// Must run before React mounts (imported at the very top of main.jsx).

export function installDomGuard() {
  if (typeof Node !== 'function' || !Node.prototype) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild(child) {
    if (child && child.parentNode !== this) {
      // The node was already detached (typically by a translation/overlay
      // extension). Return it as the spec would on success, but do not throw.
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[domGuard] skipped removeChild: node is not a child of the target', child);
      }
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      // The reference node was moved/removed out from under React. Fall back
      // to a plain append so the new node still lands in this parent.
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[domGuard] insertBefore reference not a child; appending instead', referenceNode);
      }
      return this.appendChild(newNode);
    }
    return originalInsertBefore.apply(this, arguments);
  };
}
